/**
 * Resolve which power-ups to show in the builder: stock + purchased from marketplace.
 * Uses IndexedDB (client-side).
 */

import { Puzzle } from "lucide-react";
import type { PowerUpDef } from "./catalog";
import { getStockPowerUps } from "./catalog";
import type { MarketplaceListing } from "@/types/monetization";

/** Convert a marketplace listing (power-up) to PowerUpDef for display in builder */
function listingToPowerUpDef(listing: MarketplaceListing & { powerUpId: string }): PowerUpDef {
  return {
    id: listing.powerUpId,
    label: listing.title,
    description: listing.description,
    icon: Puzzle,
    doc: "Purchased from marketplace.",
    stock: false,
    listingId: listing.id,
  };
}

/**
 * Get power-ups to show in the builder: stock (always) + marketplace ones the user has purchased.
 * Call from client only (uses IndexedDB).
 */
export async function getPowerUpsForBuilder(userId: string): Promise<PowerUpDef[]> {
  const stock = getStockPowerUps();
  if (typeof window === "undefined" || !userId) return stock;

  try {
    const { db } = await import("@/lib/db");
    if (!db?.purchases || !db?.marketplaceListings) return stock;

    const purchases = await db.purchases.where("buyerId").equals(userId).toArray();
    const listingIds = purchases.map((p) => p.listingId).filter(Boolean);
    if (listingIds.length === 0) return stock;

    const listings = await db.marketplaceListings.bulkGet(listingIds);
    const powerUpListings = listings.filter(
      (l): l is MarketplaceListing & { powerUpId: string } =>
        !!l && "powerUpId" in l && !!l.powerUpId && l.category === "power_up"
    );
    const purchasedDefs = powerUpListings.map(listingToPowerUpDef);
    const stockIds = new Set(stock.map((s) => s.id));
    const extra = purchasedDefs.filter((d) => !stockIds.has(d.id));
    return [...stock, ...extra];
  } catch {
    return stock;
  }
}

/**
 * Get listing IDs the user has purchased that are power-up listings.
 */
export async function getUnlockedPowerUpListingIds(userId: string): Promise<Set<string>> {
  const out = new Set<string>();
  if (typeof window === "undefined" || !userId) return out;
  try {
    const { db } = await import("@/lib/db");
    if (!db?.purchases || !db?.marketplaceListings) return out;
    const purchases = await db.purchases.where("buyerId").equals(userId).toArray();
    const listings = await db.marketplaceListings.bulkGet(purchases.map((p) => p.listingId));
    for (const l of listings) {
      if (l?.category === "power_up" && (l as { powerUpId?: string }).powerUpId) out.add(l.id);
    }
  } catch {
    // ignore
  }
  return out;
}
